import { useState, useEffect } from 'react';
import { relationshipsApi } from './api';
import { useAuthStore } from '../../store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';

interface Suggestion {
  type: string;
  from: string;
  to: string;
  confidence: number;
  reason: string;
  action: 'auto_create' | 'propose';
  metadata?: Record<string, any>;
}

export default function SuggestionsPanel() {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoHealLoading, setAutoHealLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  const loadSuggestions = async () => {
    if (!token || !selectedTenant) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await relationshipsApi.getSuggestions(token, selectedTenant, appId);
      setSuggestions(data.suggestions || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(false);
    }
  };

  const acceptSuggestion = async (suggestion: Suggestion) => {
    if (!token || !selectedTenant) return;
    
    try {
      await relationshipsApi.createEdge(
        suggestion.from,
        suggestion.to,
        suggestion.type,
        {
          ...suggestion.metadata,
          confidence: suggestion.confidence,
          reason: suggestion.reason,
          accepted_from_suggestion: true
        },
        token,
        selectedTenant
      );
      
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s !== suggestion));
    } catch (err: any) {
      console.error('Failed to accept suggestion:', err);
      alert(`Failed to create relationship: ${err.message}`);
    }
  };

  const dismissSuggestion = (suggestion: Suggestion) => {
    setSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  const runAutoHeal = async () => {
    if (!token || !selectedTenant) return;
    
    setAutoHealLoading(true);
    setError(null);
    
    try {
      const data = await relationshipsApi.autoHeal(token, selectedTenant);
      alert(`Auto-healed ${data.executed_count} relationships!`);
      
      // Reload suggestions to see what's left
      await loadSuggestions();
    } catch (err: any) {
      setError(err.message);
      console.error('Failed to auto-heal:', err);
      alert(`Auto-heal failed: ${err.message}`);
    } finally {
      setAutoHealLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && suggestions.length === 0 && !loading) {
      loadSuggestions();
    }
  }, [isOpen]);

  return (
    <div className="absolute bottom-4 right-4 z-10">
      {/* Toggle Button with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-lg p-4 hover:from-purple-700 hover:to-blue-700 transition-all relative"
        title="AI Suggestions"
      >
        <svg 
          className="w-6 h-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
          />
        </svg>
        
        {suggestions.length > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
            {suggestions.length}
          </div>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 bg-white rounded-lg shadow-2xl border w-96 max-h-[32rem] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🤖</span>
                <h3 className="font-semibold text-lg">AI Suggestions</h3>
              </div>
              <button
                onClick={loadSuggestions}
                disabled={loading}
                className="p-1 hover:bg-white rounded transition-colors"
                title="Refresh suggestions"
              >
                <svg 
                  className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
            </div>
            
            <p className="text-sm text-gray-600">
              Intelligent relationship recommendations based on system analysis
            </p>
          </div>

          {/* Auto-heal button */}
          {suggestions.length > 0 && (
            <div className="p-3 bg-purple-50 border-b">
              <button
                onClick={runAutoHeal}
                disabled={autoHealLoading}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
              >
                {autoHealLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Auto-healing...</span>
                  </>
                ) : (
                  <>
                    <span>⚡</span>
                    <span>Auto-heal High Confidence</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading && suggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <svg className="animate-spin h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div className="text-sm">Analyzing system...</div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-500 text-sm mb-2">⚠️ {error}</div>
                <button
                  onClick={loadSuggestions}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Try again
                </button>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">✨</div>
                <div className="text-sm font-medium mb-1">All set!</div>
                <div className="text-xs">No relationship suggestions at the moment</div>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((suggestion, idx) => (
                  <div 
                    key={idx} 
                    className="border rounded-lg p-3 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-gray-50"
                  >
                    {/* Confidence Badge */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {suggestion.type.replace(/_/g, ' ')}
                      </span>
                      <div className={`px-2 py-0.5 rounded text-xs font-bold ${
                        suggestion.confidence >= 0.9 
                          ? 'bg-green-100 text-green-800' 
                          : suggestion.confidence >= 0.75 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {Math.round(suggestion.confidence * 100)}%
                        {suggestion.action === 'auto_create' && ' ⚡'}
                      </div>
                    </div>
                    
                    {/* Connection */}
                    <div className="text-xs text-gray-600 mb-2 font-mono bg-gray-100 p-2 rounded">
                      <div className="truncate">{suggestion.from}</div>
                      <div className="text-center text-gray-400">→</div>
                      <div className="truncate">{suggestion.to}</div>
                    </div>
                    
                    {/* Reason */}
                    <div className="text-xs text-gray-700 mb-3 line-clamp-2">
                      {suggestion.reason}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptSuggestion(suggestion)}
                        className="flex-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors font-medium"
                      >
                        ✓ Accept
                      </button>
                      <button
                        onClick={() => dismissSuggestion(suggestion)}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50">
            <div className="text-xs text-gray-500 text-center">
              Powered by Relationship Agent AI
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

